import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Avatar from "../components/Avatar";
import { API } from "../api";

function Admin() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [associes, setAssocies] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [tab, setTab] = useState("soldes");
  const [form, setForm] = useState({ nom: "", prenom: "", email: "", password: "", role: "collab", associe_id: "", associe_ids: [] });
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);
  const [saved, setSaved] = useState({});
  const [search, setSearch] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [specialForm, setSpecialForm] = useState({ user_id: "", type: "", date_debut: "", date_fin: "", jours: "", commentaire: "" });
  const [specialResult, setSpecialResult] = useState("");
  const [editUser, setEditUser] = useState(null); // profil en cours d'édition
  const [editForm, setEditForm] = useState({});
  const [accrualForm, setAccrualForm] = useState({});

  useEffect(() => {
    const headers = { Authorization: "Bearer " + token };
    axios.get(API + "/users", { headers }).then(r => {
      setUsers(r.data);
      setAssocies(r.data.filter(u => u.role === "associe"));
    });
    axios.get(API + "/leaves/team", { headers }).then(r => {
      setLeaves(r.data);
    });
  }, [token]);

  const collabs = users.filter(u => (u.role === "collab" || u.role === "assistante" || u.role === "juriste" || u.role === "daf") &&
    (u.nom + " " + u.prenom).toLowerCase().includes(search.toLowerCase()));

  // Seuls les CP sont décomptés du solde (RTT/Maladie/Maternité/Paternité = sans décompte)
  const joursPoser = (userId) => leaves
    .filter(l => l.user_id === userId && l.statut === "approved" && l.type === "CP")
    .reduce((sum, l) => sum + parseFloat(l.jours || 0), 0);

  const handleChange = (id, field, value) => {
    setUsers(users.map(u => u.id === id ? { ...u, [field]: value } : u));
  };

  const handleSave = async (m) => {
    const poses = joursPoser(m.id);
    const total = parseFloat(m.cp_total) || 30;
    await axios.put(API + "/users/" + m.id + "/balance",
      { cp_total: total, cp_restants: total - poses, poste: m.poste, taux_activite: m.taux_activite },
      { headers: { Authorization: "Bearer " + token } }
    );
    setSaved({ ...saved, [m.id]: true });
    setTimeout(() => setSaved(s => ({ ...s, [m.id]: false })), 2000);
  };

  const handleRecalc = async () => {
    if (!window.confirm("Recalculer tous les soldes selon les congés CP approuvés ?\n(cp_restants = dotation − CP approuvés)")) return;
    try {
      const r = await axios.post(API + "/leaves/recalc-balances", {},
        { headers: { Authorization: "Bearer " + token } }
      );
      const { total, corrections } = r.data;
      // Recharger les soldes affichés
      const headers = { Authorization: "Bearer " + token };
      const usersRes = await axios.get(API + "/users", { headers });
      setUsers(usersRes.data);
      if (corrections.length === 0) {
        setResetSuccess(`✅ ${total} soldes vérifiés — tout était déjà cohérent.`);
      } else {
        const detail = corrections.map(c => `${c.nom} : ${c.avant}j → ${c.apres}j (${c.ecart > 0 ? "+" : ""}${c.ecart})`).join("\n");
        setResetSuccess(`✅ ${corrections.length} solde(s) corrigé(s) sur ${total} :\n${detail}`);
      }
      setTimeout(() => setResetSuccess(false), 8000);
    } catch (err) {
      setResetSuccess("❌ Erreur lors du recalcul.");
      setTimeout(() => setResetSuccess(false), 4000);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reinitialiser tous les soldes a 30 jours ?")) return;
    for (const m of collabs) {
      await axios.put(API + "/users/" + m.id + "/balance",
        { cp_total: 30, cp_restants: 30, poste: m.poste, taux_activite: m.taux_activite },
        { headers: { Authorization: "Bearer " + token } }
      );
    }
    setUsers(users.map(u => u.role === "collab" ? { ...u, cp_total: 30 } : u));
    setResetSuccess(true);
    setTimeout(() => setResetSuccess(false), 3000);
  };

  const handleExportSoldes = () => {
    const data = collabs.map(m => {
      const poses = joursPoser(m.id);
      const total = parseFloat(m.cp_total) || 30;
      const associe = [...associes, ...users.filter(u => u.role === "daf")].find(a => a.id === m.associe_id);
      return {
        "Collaborateur": m.prenom + " " + m.nom,
        "Equipe": associe ? associe.prenom + " " + associe.nom : "-",
        "Dotation annuelle": total,
        "Jours poses": poses,
        "Solde restant": parseFloat(m.cp_restants ?? (total - poses)),
        "Taux activite (%)": m.taux_activite || 100
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Soldes");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "soldes_conges.xlsx");
  };

  const handleExportConges = () => {
    const data = leaves.map(l => {
      const associe = [...associes, ...users.filter(u => u.role === "daf")].find(a => a.id === users.find(u => u.id === l.user_id)?.associe_id);
      return {
        "Collaborateur": l.prenom + " " + l.nom,
        "Equipe": associe ? associe.prenom + " " + associe.nom : "-",
        "Type": l.type,
        "Date debut": new Date(l.date_debut).toLocaleDateString("fr-FR"),
        "Date fin": new Date(l.date_fin).toLocaleDateString("fr-FR"),
        "Jours": parseFloat(l.jours),
        "Statut": l.statut === "approved" ? "Approuve" : l.statut === "refused" ? "Refuse" : "En attente",
        "Date soumission": new Date(l.created_at).toLocaleDateString("fr-FR")
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Conges");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "conges_cabinet.xlsx");
  };

  const handleCreate = async () => {
    if (!form.nom || !form.prenom || !form.email || !form.password) {
      setError("Tous les champs sont obligatoires.");
      return;
    }
    try {
      const associe_id = form.associe_ids && form.associe_ids.length > 0 ? form.associe_ids[0] : null;
      const res = await axios.post(API + "/users",
        { ...form, associe_id },
        { headers: { Authorization: "Bearer " + token } }
      );
      if (form.associe_ids && form.associe_ids.length > 1) {
        for (const aid of form.associe_ids.slice(1)) {
          await axios.post(API + "/users/" + res.data.id + "/associes",
            { associe_id: aid },
            { headers: { Authorization: "Bearer " + token } }
          );
        }
      }
      setUsers([...users, res.data]);
      if (res.data.role === "associe") setAssocies([...associes, res.data]);
      setForm({ nom: "", prenom: "", email: "", password: "", role: "collab", associe_id: "", associe_ids: [] });
      setSuccess("Compte cree avec succes !");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Erreur email deja utilise");
      setTimeout(() => setError(""), 3000);
    }
  };

  const handleAffectation = async (userId, associeId) => {
    try {
      await axios.put(API + "/users/" + userId + "/associe",
        { associe_id: associeId },
        { headers: { Authorization: "Bearer " + token } }
      );
      setUsers(users.map(u => u.id === userId ? { ...u, associe_id: parseInt(associeId) } : u));
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm("Supprimer " + name + " ?")) return;
    try {
      await axios.delete(API + "/users/" + id,
        { headers: { Authorization: "Bearer " + token } }
      );
      setUsers(prev => prev.filter(u => u.id !== id));
      setLeaves(prev => prev.filter(l => l.user_id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const tabStyle = (t) => ({
    padding: "8px 20px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 13,
    fontWeight: tab === t ? 600 : 400,
    background: tab === t ? "#7C6FE0" : "#f5f5f5",
    color: tab === t ? "#fff" : "#555"
  });

  const TH = ({ children }) => (
    <th style={{ textAlign: "left", padding: "6px 10px", fontSize: 11, color: "#888", borderBottom: "1px solid #eee" }}>
      {children}
    </th>
  );

  return (
    <div style={{ maxWidth: 960 }}>
      <h2 style={{ marginBottom: 20, fontWeight: 500 }}>Administration</h2>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
        <button style={tabStyle("soldes")} onClick={() => setTab("soldes")}>Soldes</button>
        <button style={tabStyle("equipes")} onClick={() => setTab("equipes")}>Equipes</button>
        <button style={tabStyle("utilisateurs")} onClick={() => setTab("utilisateurs")}>Utilisateurs</button>
        <button style={tabStyle("nouveau")} onClick={() => setTab("nouveau")}>+ Nouveau compte</button>
        <button style={tabStyle("import")} onClick={() => setTab("import")}>⬆ Import CSV</button>
        <button style={tabStyle("special")} onClick={() => setTab("special")}>📋 Congés spéciaux</button>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
          style={{ marginLeft: "auto", padding: "7px 12px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, width: 180 }} />
      </div>

      {tab === "soldes" && (
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: "#888" }}>Soldes annuels des collaborateurs</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleExportSoldes} style={{ background: "#EAF3DE", color: "#3B6D11", border: "1px solid #97C459", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>
                Export soldes Excel
              </button>
              <button onClick={handleExportConges} style={{ background: "#E6F1FB", color: "#185FA5", border: "1px solid #85B7EB", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>
                Export conges Excel
              </button>
              <button onClick={handleRecalc} style={{ background: "#EEEDFE", color: "#5B4FC7", border: "1px solid #B9B0F0", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>
                🔄 Recalculer les soldes
              </button>
              <button onClick={handleReset} style={{ background: "#FCEBEB", color: "#A32D2D", border: "1px solid #F09595", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>
                Reinitialiser a 30j
              </button>
            </div>
          </div>
          {resetSuccess && (
            <div style={{ background: "#EAF3DE", color: "#3B6D11", padding: "8px 12px", borderRadius: 6, marginBottom: 12, fontSize: 13, whiteSpace: "pre-line" }}>
              {resetSuccess === true ? "Tous les soldes reinitialises a 30 jours." : resetSuccess}
            </div>
          )}
          <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 10, padding: 16 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  <TH>Collaborateur</TH>
                  <TH>Equipe</TH>
                  <TH>Dotation annuelle</TH>
                  <TH>Jours poses</TH>
                  <TH>Solde restant</TH>
                  <TH>Enregistrer</TH>
                </tr>
              </thead>
              <tbody>
                {collabs.map(m => {
                  const poses = joursPoser(m.id);
                  const total = parseFloat(m.cp_total) || (m.accrual_rate ? 0 : 30);
                  const solde = parseFloat(m.cp_restants ?? (total - poses));
                  const associe = [...associes, ...users.filter(u => u.role === "daf")].find(a => a.id === m.associe_id);
                  const accrualStart = m.accrual_start ? new Date(m.accrual_start) : null;
                  const notYetArrived = accrualStart && accrualStart > new Date();
                  return (
                    <tr key={m.id}>
                      <td style={td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Avatar prenom={m.prenom} nom={m.nom} size={28} fontSize={11} />
                          <div>
                            <strong>{m.prenom} {m.nom}</strong>
                            {notYetArrived && (
                              <div style={{ fontSize: 10, color: "#B45309", background: "#FEF3C7", borderRadius: 10, padding: "1px 6px", marginTop: 2, display: "inline-block" }}>
                                Arrive le {accrualStart.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={td}>{associe ? associe.prenom + " " + associe.nom : <span style={{ color: "#bbb" }}>-</span>}</td>
                      <td style={td}>
                        <input type="number" value={m.cp_total || 30} min={0} max={100}
                          onChange={e => handleChange(m.id, "cp_total", e.target.value)}
                          style={{ width: 55, padding: "3px 6px", border: "1px solid #ddd", borderRadius: 4, fontSize: 12 }} />
                      </td>
                      <td style={td}><span style={{ color: poses > 0 ? "#854F0B" : "#888" }}>{poses} j</span></td>
                      <td style={td}><span style={{ fontWeight: 600, color: solde <= 5 ? "#A32D2D" : "#3B6D11" }}>{solde} j</span></td>
                      <td style={td}>
                        <button onClick={() => handleSave(m)}
                          style={{ background: saved[m.id] ? "#EAF3DE" : "#f5f5f5", color: saved[m.id] ? "#3B6D11" : "#444", border: "1px solid #ddd", borderRadius: 4, padding: "3px 10px", fontSize: 11, cursor: "pointer" }}>
                          {saved[m.id] ? "OK" : "Enreg."}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "equipes" && (
        <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>
            Affectez chaque collaborateur à un ou plusieurs responsables. Le premier est le responsable principal.
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                <TH>Collaborateur</TH>
                <TH>Responsables actuels</TH>
                <TH>Ajouter un responsable</TH>
              </tr>
            </thead>
            <tbody>
              {collabs.map(u => {
                const allResps = u.role === "assistante"
                  ? users.filter(x => x.role === "daf")
                  : associes;
                const currentIds = u.all_associe_ids || (u.associe_id ? [u.associe_id] : []);
                const available = allResps.filter(a => !currentIds.includes(a.id));
                return (
                  <tr key={u.id}>
                    <td style={td}>{u.prenom} {u.nom}</td>
                    <td style={td}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {currentIds.length === 0 && <span style={{ color: "#bbb", fontSize: 12 }}>— Non affecté —</span>}
                        {currentIds.map((aid, idx) => {
                          const a = allResps.find(x => x.id === aid);
                          if (!a) return null;
                          const isPrimary = aid === u.associe_id;
                          return (
                            <span key={aid} style={{
                              display: "inline-flex", alignItems: "center", gap: 5,
                              background: isPrimary ? "#EDE9FE" : "#F3F4F6",
                              color: isPrimary ? "#6D28D9" : "#555",
                              borderRadius: 20, padding: "3px 10px 3px 8px",
                              fontSize: 12, fontWeight: isPrimary ? 600 : 400,
                              border: `1px solid ${isPrimary ? "#C4B5FD" : "#ddd"}`
                            }}>
                              {isPrimary && <span style={{ fontSize: 10 }}>★</span>}
                              {a.prenom} {a.nom}
                              <button onClick={async () => {
                                const headers = { Authorization: "Bearer " + token };
                                if (isPrimary) {
                                  // Promote next one if exists, otherwise null
                                  const next = currentIds.find(id => id !== aid) || null;
                                  await axios.put(`${API}/users/${u.id}/associe`, { associe_id: next }, { headers });
                                  if (currentIds.length > 1) {
                                    // Remove from user_associes too if it was there
                                    await axios.delete(`${API}/users/${u.id}/associes/${aid}`, { headers }).catch(() => {});
                                  }
                                  setUsers(users.map(x => x.id === u.id ? {
                                    ...x,
                                    associe_id: next,
                                    all_associe_ids: currentIds.filter(id => id !== aid)
                                  } : x));
                                } else {
                                  await axios.delete(`${API}/users/${u.id}/associes/${aid}`, { headers });
                                  setUsers(users.map(x => x.id === u.id ? {
                                    ...x,
                                    all_associe_ids: currentIds.filter(id => id !== aid)
                                  } : x));
                                }
                              }} style={{
                                background: "none", border: "none", cursor: "pointer",
                                color: "#999", fontSize: 14, lineHeight: 1, padding: 0, marginLeft: 2
                              }}>×</button>
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td style={td}>
                      {available.length > 0 && (
                        <select defaultValue="" onChange={async e => {
                          const newId = parseInt(e.target.value);
                          if (!newId) return;
                          e.target.value = "";
                          const headers = { Authorization: "Bearer " + token };
                          if (currentIds.length === 0) {
                            await axios.put(`${API}/users/${u.id}/associe`, { associe_id: newId }, { headers });
                            setUsers(users.map(x => x.id === u.id ? { ...x, associe_id: newId, all_associe_ids: [newId] } : x));
                          } else {
                            await axios.post(`${API}/users/${u.id}/associes`, { associe_id: newId }, { headers });
                            setUsers(users.map(x => x.id === u.id ? { ...x, all_associe_ids: [...currentIds, newId] } : x));
                          }
                        }} style={{ padding: "5px 8px", border: "1px solid #ddd", borderRadius: 6, fontSize: 12, color: "#555" }}>
                          <option value="">+ Ajouter...</option>
                          {available.map(a => (
                            <option key={a.id} value={a.id}>{a.prenom} {a.nom}</option>
                          ))}
                        </select>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === "utilisateurs" && (
        <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 12, padding: 16 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                <TH>Nom</TH>
                <TH>Email</TH>
                <TH>Rôle</TH>
                <TH>Poste</TH>
                <TH>Taux</TH>
                <TH>Cumul/mois</TH>
                <TH>Actions</TH>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td style={td}>{u.prenom} {u.nom}</td>
                  <td style={td}>{u.email}</td>
                  <td style={td}>
                    <span style={{ background: u.role === "daf" ? "#EEEDFE" : u.role === "associe" ? "#E6F1FB" : "#f5f5f5", color: u.role === "daf" ? "#7C6FE0" : u.role === "associe" ? "#185FA5" : "#555", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 500 }}>
                      {u.role}
                    </span>
                  </td>
                  <td style={td}>{u.poste || "-"}</td>
                  <td style={td}>{u.taux_activite || 100}%</td>
                  <td style={td}>{u.accrual_rate ? <span style={{ background: "#EDE9FE", color: "#6D28D9", borderRadius: 12, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>{u.accrual_rate}j/mois</span> : <span style={{ color: "#ccc" }}>—</span>}</td>
                  <td style={{ ...td, display: "flex", gap: 6 }}>
                    <button onClick={() => { setEditUser(u); setEditForm({ prenom: u.prenom, nom: u.nom, email: u.email, poste: u.poste || "", taux_activite: u.taux_activite || 100 }); setAccrualForm({ accrual_rate: u.accrual_rate || "", accrual_start: u.accrual_start ? u.accrual_start.slice(0,10) : "" }); }}
                      style={{ background: "#EDE9FE", color: "#6D28D9", border: "1px solid #C4B5FD", borderRadius: 4, padding: "3px 10px", fontSize: 11, cursor: "pointer" }}>
                      ✏️ Modifier
                    </button>
                    {u.role !== "daf" && (
                      <button onClick={() => handleDelete(u.id, u.prenom + " " + u.nom)}
                        style={{ background: "#FCEBEB", color: "#A32D2D", border: "1px solid #F09595", borderRadius: 4, padding: "3px 10px", fontSize: 11, cursor: "pointer" }}>
                        Supprimer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal édition profil */}
      {editUser && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 420, boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 20 }}>✏️ Modifier le profil — {editUser.prenom} {editUser.nom}</div>
            {[
              { label: "Prénom", key: "prenom" },
              { label: "Nom", key: "nom" },
              { label: "Email", key: "email" },
              { label: "Poste", key: "poste" },
              { label: "Taux d'activité (%)", key: "taux_activite", type: "number" },
            ].map(({ label, key, type }) => (
              <div key={key} style={{ marginBottom: 12 }}>
                <label style={lbl}>{label}</label>
                <input type={type || "text"} value={editForm[key] || ""} onChange={e => setEditForm({ ...editForm, [key]: e.target.value })} style={inp} />
              </div>
            ))}
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Rôle</label>
              <select value={editForm.role || editUser?.role || ""} onChange={e => {
                const role = e.target.value;
                let newAccrual = { ...accrualForm };
                if (role === "juriste") newAccrual = { accrual_rate: "2.5", accrual_start: accrualForm.accrual_start || new Date().toISOString().slice(0,10) };
                else if (role === "assistante") newAccrual = { accrual_rate: accrualForm.accrual_rate || "", accrual_start: accrualForm.accrual_start || "" };
                else newAccrual = { accrual_rate: "", accrual_start: "" };
                setEditForm({ ...editForm, role });
                setAccrualForm(newAccrual);
              }} style={inp}>
                <option value="collab">Collaborateur</option>
                <option value="juriste">Juriste</option>
                <option value="assistante">Assistante</option>
                <option value="associe">Associé</option>
                <option value="daf">DAF</option>
              </select>
              {(editForm.role || editUser?.role) === "juriste" && (
                <div style={{ fontSize: 11, color: "#7C6FE0", marginTop: 4 }}>Cumul automatique 2.5j/mois pré-rempli</div>
              )}
            </div>
            {/* Cumul mensuel */}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #eee" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#888", marginBottom: 10 }}>CUMUL MENSUEL (optionnel)</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label style={lbl}>Jours / mois</label>
                  <input type="number" step="0.01" value={accrualForm.accrual_rate} onChange={e => setAccrualForm({ ...accrualForm, accrual_rate: e.target.value })} placeholder="ex: 2.08" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Date de début</label>
                  <input type="date" value={accrualForm.accrual_start} onChange={e => setAccrualForm({ ...accrualForm, accrual_start: e.target.value })} style={inp} />
                </div>
              </div>
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>En fin de mois, le solde sera automatiquement crédité du nombre de jours défini (aligné sur la fin de paie).</div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={async () => {
                const headers = { Authorization: "Bearer " + token };
                try {
                  const resp = await axios.put(`${API}/users/${editUser.id}/profile`, editForm, { headers });
                  if (accrualForm.accrual_rate) {
                    await axios.put(`${API}/users/${editUser.id}/accrual`, accrualForm, { headers });
                  }
                  setUsers(users.map(u => u.id === editUser.id ? { ...u, ...resp.data, ...accrualForm } : u));
                  setEditUser(null);
                } catch (e) {
                  alert("Erreur lors de la modification");
                }
              }} style={{ flex: 1, background: "#7C6FE0", color: "#fff", border: "none", borderRadius: 8, padding: "10px 0", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>
                Enregistrer
              </button>
              <button onClick={() => setEditUser(null)}
                style={{ flex: 1, background: "#f5f5f5", color: "#555", border: "1px solid #ddd", borderRadius: 8, padding: "10px 0", fontWeight: 500, cursor: "pointer", fontSize: 14 }}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === "special" && (
        <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 12, padding: 24, maxWidth: 500 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Saisir un congé (DAF)</div>
          <div style={{ fontSize: 12, color: specialForm.type === "CP" ? "#854F0B" : "#888", marginBottom: 16 }}>
            {specialForm.type === "CP"
              ? "⚠️ CP accordé par anticipation : saisi comme approuvé et DÉCOMPTÉ du solde de la personne (peut le rendre négatif si les droits ne sont pas encore acquis)."
              : "Congé maternité, paternité, maladie, RTT, etc. Saisi directement comme approuvé, sans impact sur le solde CP."}
          </div>
          {specialResult && (
            <div style={{ background: specialResult.startsWith("✅") ? "#EAF3DE" : "#FCEBEB", color: specialResult.startsWith("✅") ? "#3B6D11" : "#A32D2D", padding: "8px 12px", borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
              {specialResult}
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>Collaborateur</label>
            <select value={specialForm.user_id} onChange={e => setSpecialForm({ ...specialForm, user_id: e.target.value })} style={inp}>
              <option value="">-- Sélectionner --</option>
              {users.filter(u => u.role !== "daf").map(u => (
                <option key={u.id} value={u.id}>{u.prenom} {u.nom} ({u.role})</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>Type de congé</label>
            <select value={specialForm.type} onChange={e => setSpecialForm({ ...specialForm, type: e.target.value })} style={inp}>
              <option value="">-- Sélectionner --</option>
              <option value="CP">CP — accordé par anticipation (décompté du solde)</option>
              <option value="Maladie">Maladie</option>
              <option value="Maternite">Maternité</option>
              <option value="Paternite">Paternité</option>
              <option value="RTT">Congés spéciaux / RTT</option>
              <option value="Pacs">PACS</option>
              <option value="Sans solde">Sans solde</option>
            </select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={lbl}>Date début</label>
              <input type="date" value={specialForm.date_debut} onChange={e => setSpecialForm({ ...specialForm, date_debut: e.target.value })} style={inp} />
            </div>
            <div>
              <label style={lbl}>Date fin</label>
              <input type="date" value={specialForm.date_fin} onChange={e => setSpecialForm({ ...specialForm, date_fin: e.target.value })} style={inp} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>Nombre de jours</label>
            <input type="number" value={specialForm.jours} onChange={e => setSpecialForm({ ...specialForm, jours: e.target.value })}
              min="0.5" step="0.5" style={inp} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={lbl}>Commentaire (optionnel)</label>
            <input value={specialForm.commentaire} onChange={e => setSpecialForm({ ...specialForm, commentaire: e.target.value })} style={inp} />
          </div>
          <button onClick={async () => {
            if (!specialForm.user_id || !specialForm.type || !specialForm.date_debut || !specialForm.date_fin || !specialForm.jours) {
              setSpecialResult("❌ Tous les champs sont obligatoires.");
              return;
            }
            try {
              await axios.post(API + "/leaves/admin", specialForm,
                { headers: { Authorization: "Bearer " + token } });
              setSpecialResult("✅ Congé enregistré avec succès.");
              setSpecialForm({ user_id: "", type: "", date_debut: "", date_fin: "", jours: "", commentaire: "" });
              setTimeout(() => setSpecialResult(""), 4000);
            } catch (err) {
              setSpecialResult("❌ " + (err.response?.data?.error || "Erreur serveur"));
            }
          }} style={{ background: "#7C6FE0", color: "#fff", border: "none", borderRadius: 6, padding: "10px 24px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Enregistrer
          </button>
        </div>
      )}

      {tab === "import" && (
        <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 12, padding: 24, maxWidth: 560 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Import en masse depuis un fichier CSV</div>
          <div style={{ background: "#f9f9f9", border: "1px solid #e0e0e0", borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 12, color: "#666", lineHeight: 1.7 }}>
            Format requis (1 ligne par collaborateur) :<br />
            <code style={{ background: "#eee", padding: "2px 6px", borderRadius: 4 }}>prenom,nom,email,mot_de_passe,role</code><br />
            Rôles acceptés : <code>collab</code>, <code>assistante</code>, <code>associe</code>, <code>daf</code><br /><br />
            Exemple :<br />
            <code>Marie,Dupont,m.dupont@swavocats.com,MotDePasse1,collab</code>
          </div>
          <input type="file" accept=".csv,.txt" onChange={async (e) => {
            const file = e.target.files[0];
            if (!file) return;
            const text = await file.text();
            const lines = text.split("\n").filter(l => l.trim());
            const users = lines.map(line => {
              const [prenom, nom, email, password, role] = line.split(",").map(s => s.trim());
              return { prenom, nom, email, password, role };
            }).filter(u => u.email);
            if (users.length === 0) { setImportResult({ error: "Fichier vide ou format invalide" }); return; }
            try {
              const res = await axios.post(API + "/users/import", { users },
                { headers: { Authorization: "Bearer " + token } });
              setImportResult(res.data);
              if (res.data.created > 0) {
                const r = await axios.get(API + "/users", { headers: { Authorization: "Bearer " + token } });
                setUsers(r.data);
              }
            } catch (err) {
              setImportResult({ error: "Erreur lors de l'import" });
            }
            e.target.value = "";
          }} style={{ marginBottom: 16 }} />
          {importResult && (
            <div style={{ background: importResult.error ? "#FCEBEB" : "#EAF3DE", color: importResult.error ? "#A32D2D" : "#3B6D11", padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>
              {importResult.error ? importResult.error : (
                <>
                  ✅ {importResult.created} compte(s) créé(s)
                  {importResult.errors && importResult.errors.length > 0 && (
                    <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                      {importResult.errors.map((e, i) => <li key={i} style={{ color: "#A32D2D" }}>{e}</li>)}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "nouveau" && (
        <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 12, padding: 24, maxWidth: 460 }}>
          {error && <div style={{ background: "#FCEBEB", color: "#A32D2D", padding: "8px 12px", borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{error}</div>}
          {success && <div style={{ background: "#EAF3DE", color: "#3B6D11", padding: "8px 12px", borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{success}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={lbl}>Prenom</label>
              <input value={form.prenom} onChange={e => setForm({ ...form, prenom: e.target.value })} style={inp} />
            </div>
            <div>
              <label style={lbl}>Nom</label>
              <input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} style={inp} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>Email</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inp} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>Mot de passe provisoire</label>
            <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} style={inp} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>Role</label>
            <select value={form.role} onChange={e => {
              const role = e.target.value;
              if (role === "assistante") {
                const daf = users.find(u => u.role === "daf");
                setForm({ ...form, role, associe_ids: daf ? [daf.id] : [], associe_id: daf ? daf.id : "" });
              } else {
                setForm({ ...form, role, associe_ids: [] });
              }
            }} style={inp}>
              <option value="collab">Collaborateur</option>
              <option value="associe">Associe</option>
              <option value="daf">DAF</option>
              <option value="assistante">Assistante</option>
            </select>
          </div>
          {form.role === "collab" && (
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Responsables (plusieurs possibles)</label>
              <div style={{ border: "1px solid #ddd", borderRadius: 6, padding: 8, maxHeight: 150, overflowY: "auto" }}>
                {[...associes, ...users.filter(u => u.role === "daf")].map(a => (
                  <label key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 13, cursor: "pointer" }}>
                    <input type="checkbox"
                      checked={form.associe_ids?.includes(a.id) || false}
                      onChange={e => {
                        const ids = form.associe_ids || [];
                        if (e.target.checked) {
                          setForm({ ...form, associe_ids: [...ids, a.id], associe_id: ids.length === 0 ? a.id : form.associe_id });
                        } else {
                          setForm({ ...form, associe_ids: ids.filter(id => id !== a.id) });
                        }
                      }} />
                    {a.prenom} {a.nom} ({a.role})
                  </label>
                ))}
              </div>
            </div>
          )}
          <button onClick={handleCreate}
            style={{ background: "#7C6FE0", color: "#fff", border: "none", borderRadius: 6, padding: "10px 24px", fontSize: 13, fontWeight: 600, cursor: "pointer", marginTop: 8 }}>
            Creer le compte
          </button>
        </div>
      )}
    </div>
  );
}

const td = { padding: "8px 10px", borderBottom: "1px solid #f5f5f5" };
const lbl = { fontSize: 12, color: "#666", display: "block", marginBottom: 4 };
const inp = { width: "100%", padding: "7px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, boxSizing: "border-box" };

export default Admin;